import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";

interface FieldDef {
  key: string;
  label: string;
  type?: "text" | "textarea";
}

interface BlockDef {
  page: string;
  block: string;
  label: string;
  fields: FieldDef[];
}

const BLOCKS: BlockDef[] = [
  {
    page: "home",
    block: "trust_bar",
    label: "Home — Barra de confianza",
    fields: [
      { key: "titulo", label: "Título" },
      { key: "descripcion", label: "Descripción" },
      { key: "icono", label: "Ícono (clase remixicon, ej. ri-shield-check-line)" },
    ],
  },
  {
    page: "home",
    block: "steps",
    label: "Home — Cómo funciona (pasos)",
    fields: [
      { key: "numero", label: "Número (ej. 01)" },
      { key: "titulo", label: "Título" },
      { key: "descripcion", label: "Descripción", type: "textarea" },
      { key: "icono", label: "Ícono" },
    ],
  },
  {
    page: "home",
    block: "benefits",
    label: "Home — Beneficios",
    fields: [
      { key: "titulo", label: "Título" },
      { key: "descripcion", label: "Descripción", type: "textarea" },
      { key: "icono", label: "Ícono" },
    ],
  },
  {
    page: "home",
    block: "stats",
    label: "Home — Stats del recinto",
    fields: [
      { key: "valor", label: "Valor (ej. 12 m²)" },
      { key: "etiqueta", label: "Etiqueta (ej. Desde)" },
    ],
  },
  {
    page: "nosotros",
    block: "valores",
    label: "Nosotros — Valores",
    fields: [
      { key: "titulo", label: "Título" },
      { key: "descripcion", label: "Descripción", type: "textarea" },
      { key: "icono", label: "Ícono" },
    ],
  },
  {
    page: "seguridad",
    block: "medidas",
    label: "Seguridad — Medidas",
    fields: [
      { key: "titulo", label: "Título" },
      { key: "descripcion", label: "Descripción", type: "textarea" },
      { key: "icono", label: "Ícono" },
    ],
  },
  {
    page: "ubicacion",
    block: "como_llegar",
    label: "Ubicación — Cómo llegar",
    fields: [
      { key: "desde", label: "Desde (ej. Desde Puerto Varas)" },
      { key: "ruta", label: "Descripción de la ruta", type: "textarea" },
      { key: "tiempo", label: "Tiempo (ej. ≈ 10 min)" },
    ],
  },
  {
    page: "ubicacion",
    block: "accesos",
    label: "Ubicación — Accesos",
    fields: [
      { key: "titulo", label: "Título" },
      { key: "descripcion", label: "Descripción" },
      { key: "icono", label: "Ícono" },
    ],
  },
];

type Item = { id: string; [key: string]: string };

export default function AdminContentBlocks() {
  const [selected, setSelected] = useState(BLOCKS[0]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  async function load(def: BlockDef) {
    setLoading(true);
    setMessage(null);
    const res = await fetch(`${API_BASE}/bootstrap`);
    const json = await res.json();
    const blockItems = json.contentBlocks?.[def.page]?.[def.block] ?? [];
    setItems(blockItems);
    setLoading(false);
  }

  useEffect(() => {
    load(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  function updateField(index: number, key: string, value: string) {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [key]: value } : it)));
  }

  function addItem() {
    const empty: Item = { id: `item-${Date.now()}` };
    for (const f of selected.fields) empty[f.key] = "";
    setItems((prev) => [...prev, empty]);
  }

  function removeItem(index: number) {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  function move(index: number, dir: -1 | 1) {
    setItems((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/content-blocks/${selected.page}/${selected.block}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        setMessage({ type: "error", text: "No se pudo guardar" });
        return;
      }
      setMessage({ type: "success", text: "Cambios guardados." });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Bloques de contenido</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Listas que se repiten en distintas páginas: barra de confianza, pasos, beneficios, valores, medidas, etc.
      </p>

      <select
        className="mt-4 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm"
        value={`${selected.page}/${selected.block}`}
        onChange={(e) => {
          const found = BLOCKS.find((b) => `${b.page}/${b.block}` === e.target.value);
          if (found) setSelected(found);
        }}
      >
        {BLOCKS.map((b) => (
          <option key={`${b.page}/${b.block}`} value={`${b.page}/${b.block}`}>
            {b.label}
          </option>
        ))}
      </select>

      {message && (
        <div
          className={`mt-4 rounded-md p-3 text-sm border ${
            message.type === "success" ? "bg-primary-100 border-primary-200 text-primary-800" : "bg-accent-100 border-accent-200 text-accent-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-foreground-500">Cargando...</p>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((item, i) => (
            <div key={item.id} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-foreground-400">#{i + 1}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => move(i, -1)} className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer">
                    ↑
                  </button>
                  <button type="button" onClick={() => move(i, 1)} className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer">
                    ↓
                  </button>
                  <button type="button" onClick={() => removeItem(i)} className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer">
                    Eliminar
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {selected.fields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea
                        className={inputCls}
                        rows={2}
                        value={item[f.key] ?? ""}
                        onChange={(e) => updateField(i, f.key, e.target.value)}
                      />
                    ) : (
                      <input className={inputCls} value={item[f.key] ?? ""} onChange={(e) => updateField(i, f.key, e.target.value)} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addItem}
            className="w-full rounded-lg border-2 border-dashed border-background-300 py-3 text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-600 cursor-pointer"
          >
            + Agregar item
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
